import { FormCard } from './ui'

/** Page heading for all FNOL post-submit confirmation screens. */
export const CLAIM_SUBMITTED_HEADING = 'Thanks for notifying us of your claim'

/**
 * Shown on windscreen submit confirmation when the customer chose their own repairer
 * (CSP-310) or glass was repaired/replaced before lodgement.
 */
export const WINDSCREEN_PRIOR_REPAIR_REIMBURSEMENT_COPY =
  'Send us a copy of your paid repairer invoice to claims@club4x4.com.au along with your BSB, Account Number and Account Name for reimbursement.'

export type ClaimSubmittedBranch = 'windscreen' | 'other'

export interface ClaimSubmittedSummaryCardProps {
  branch: ClaimSubmittedBranch
  claimNumber: string | null
  /** From {@link formatBasicExcessDisplay} (e.g. `$500`). */
  basicExcess: string
  /** Windscreen only — show excess-waiver copy when the policy includes excess-free glass cover. */
  hasExcessFreeGlassCover?: boolean
  /** Windscreen only — glass was repaired/replaced before this claim (prior-repair path). */
  windscreenAlreadyRepaired?: boolean
  /** Windscreen only — customer chose own repairer (not NWG); CSP-310. */
  windscreenOwnRepairer?: boolean
}

/**
 * Shared claim number, basic excess, and renewal copy after successful FNOL submit.
 */
export function ClaimSubmittedSummaryCard({
  branch,
  claimNumber,
  basicExcess,
  hasExcessFreeGlassCover = false,
  windscreenAlreadyRepaired = false,
  windscreenOwnRepairer = false,
}: ClaimSubmittedSummaryCardProps) {
  const showPriorRepairReimbursementCopy =
    branch === 'windscreen' && (windscreenAlreadyRepaired || windscreenOwnRepairer)

  return (
    <FormCard>
      <div className="flex flex-col gap-4">
        {claimNumber ? (
          <p className="m-0 font-body text-sm leading-sm text-text-secondary-700">
            Your claim number is:{' '}
            <strong className="font-semibold text-text-primary-900">{claimNumber}</strong>
          </p>
        ) : null}
        {showPriorRepairReimbursementCopy ? (
          <p className="m-0 font-body text-sm leading-sm text-text-secondary-700">
            {WINDSCREEN_PRIOR_REPAIR_REIMBURSEMENT_COPY}
          </p>
        ) : (
          <p className="m-0 font-body text-sm leading-sm text-text-secondary-700">
            Your claim will need to be reviewed and finalised by our team. Please expect a call from us
            within 2 business days.
          </p>
        )}
        <p className="m-0 font-body text-sm leading-sm text-text-secondary-700">
          Your basic excess is {basicExcess}
        </p>
        {branch === 'windscreen' && hasExcessFreeGlassCover ? (
          <p className="m-0 font-body text-sm leading-sm text-text-secondary-700">
            Depending on your level of cover and previous claims this excess may not be payable – we will let you know
            if this applies to you when we finalise your lodgement
          </p>
        ) : branch === 'other' ? (
          <p className="m-0 font-body text-sm leading-sm text-text-secondary-700">
            In some instances, there may be an additional excess to pay – when we contact you to finalise your
            lodgement we will let you know if this applies to you.
          </p>
        ) : null}
        <p className="m-0 font-body text-sm leading-sm text-text-secondary-700">
          Please note that by lodging a claim there may be an impact to your premium at renewal.
        </p>
      </div>
    </FormCard>
  )
}
